import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import AuthShell from '../components/AuthShell';
import { useAuth } from '../lib/auth';
import { ApiError } from '../lib/api';

export default function Activate() {
    const { t } = useTranslation();
    const [params] = useSearchParams();
    const { activate } = useAuth();
    const navigate = useNavigate();

    const token = params.get('t') || '';
    const [pw,  setPw]  = useState('');
    const [pw2, setPw2] = useState('');
    const [err, setErr] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    async function onSubmit(e: React.FormEvent) {
        e.preventDefault();
        setErr(null);
        if (pw.length < 8) { setErr(t('auth.errors.passwordTooShort')); return; }
        if (pw !== pw2)    { setErr(t('auth.errors.passwordsMismatch')); return; }
        if (!token)        { setErr(t('auth.errors.tokenInvalid')); return; }
        setBusy(true);
        try {
            await activate(token, pw);
            navigate('/app');
        } catch (e) {
            const msg = e instanceof ApiError && e.code === 'BadToken'
                ? t('auth.errors.tokenInvalid')
                : (e instanceof Error ? e.message : t('auth.errors.unknown'));
            setErr(msg);
        } finally { setBusy(false); }
    }

    return (
        <AuthShell
            eyebrow={t('brand.tagline')}
            title={t('auth.activate.title')}
            subtitle={t('auth.activate.sub')}
            backTo="/"
        >
            <form onSubmit={onSubmit} className="space-y-5" noValidate>
                <Field label={t('auth.activate.newPw')}    type="password" value={pw}  onChange={setPw}  autoComplete="new-password" />
                <Field label={t('auth.activate.confirm')} type="password" value={pw2} onChange={setPw2} autoComplete="new-password" />
                {err && <ErrorLine msg={err} />}
                <button type="submit" className="btn btn-primary w-full" disabled={busy}>
                    {busy ? t('common.loading') : t('auth.activate.cta')}
                </button>
            </form>
            <div className="mt-6 text-center">
                <Link to="/" className="btn-link text-sm">{t('common.cancel')}</Link>
            </div>
        </AuthShell>
    );
}

function Field({ label, type, value, onChange, autoComplete }: {
    label: string; type: 'password' | 'text'; value: string; onChange: (v: string) => void; autoComplete?: string;
}) {
    return (
        <label className="block">
            <span className="field-label">{label}</span>
            <input className="field-input"
                   type={type} value={value} autoComplete={autoComplete}
                   onChange={(e) => onChange(e.target.value)} required />
        </label>
    );
}

function ErrorLine({ msg }: { msg: string }) {
    return (
        <div role="alert" className="text-sm text-ember pl-3 border-l-2 border-ember py-1">{msg}</div>
    );
}
